/**
 * What the télé-expertise screen reads, and how it renders the two vocabularies it mixes.
 *
 * There are two, and keeping them apart is the point of this module:
 *
 * - **the sending form's** — `priorite_proposee`, `niveau`, the zones. Open vocabularies that belong
 *   to IMeD. A value we have never seen is shown verbatim rather than dropped, because the day they
 *   add one is the day a dossier would otherwise vanish from the screen it was sent to.
 * - **ours** — `triage_statut`, `suite_donnee` and the signal `axe`. Closed, because a queue is
 *   driven by them.
 *
 * The French labels of the first come from the API, which is where their vocabulary is known. The
 * labels of the second are translated here, being ours.
 */

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/macro";

export type TriageStatut = "recu" | "avis_rendu";
export type SuiteDonnee = "consultation_proposee" | "sans_consultation";

export interface SignalAxisDescriptor {
  axe: string;
  libelle: string;
  description: string;
}

export interface Signal {
  question: string;
  niveau: string | null;
  libelle: string | null;
  axe: string;
  /** The short tag this signal earns in the list (ET3) — only the four strong ones have one. */
  tag?: string | null;
  /** The wording the patient was shown — absent when the dossier names a question it has no label for. */
  question_posee?: string | null;
  /** What the patient answered. `null` when the signal quotes a question the dossier does not hold. */
  reponse?: string | number | boolean | null;
}

export interface StrongTag {
  question: string;
  libelle: string;
  axe: string;
}

export interface Zone {
  cle: string;
  libelle: string;
}

export interface Score {
  nom: string;
  titre: string;
  score_pct: number | null;
  classe: string | null;
  somme?: number | null;
  rubriques_renseignees?: number | null;
}

export interface PatientLink {
  id_magex: string;
  tenant_id: string;
  id_patient: number;
  nom: string | null;
  prenom: string | null;
  date_naissance: string | null;
  nir_masque: string | null;
}

/** What the PATIENT typed about themselves, available from the moment the dossier arrives. */
export interface DeclaredName {
  nom: string | null;
  prenom: string | null;
}

/** One of this person's other demands: where it stands, and where to go and read it. */
export interface DemandeVoisine {
  dossier_id: string;
  recu_le: string | null;
  triage_statut: TriageStatut;
  triage_statut_libelle: string | null;
  triage_le: string | null;
  suite_donnee: SuiteDonnee | null;
  suite_donnee_libelle: string | null;
}

/**
 * The other demands this person has sent — the counterpart of the refusal at the door.
 *
 * A second dossier is refused while the first waits for an avis, and accepted once the avis is
 * rendered. So a person appearing twice in this queue is always a person whose earlier demand was
 * answered, and that answer is the context the dossier on screen does not carry.
 *
 * `null` on the whole field when there is only one: a marker that is always there is a marker a
 * reader stops seeing.
 */
export interface Historique {
  rang: number;
  total: number;
  /**
   * The neighbours on either side, because a dossier is read from both halves of the queue: opened
   * from « reçu » the useful question is what came before, opened from the answered history it is
   * what came after. Each is null at its end of the series, and `rang` / `total` say which end.
   */
  precedent: DemandeVoisine | null;
  suivant: DemandeVoisine | null;
}

export interface DossierSummary {
  dossier_id: string;
  submission_id: string;
  recu_le: string | null;
  priorite_proposee: string | null;
  priorite_libelle: string | null;
  signaux_forts: number;
  signaux_total: number;
  tags_forts: StrongTag[];
  axes: Record<string, number>;
  /** At least one STRONG signal that sets a delay — as opposed to one that states an intention. */
  urgence_clinique: boolean;
  zones: Zone[];
  mineur: boolean;
  scores: Score[];
  medecin_rpps: string | null;
  medecin_adeli: string | null;
  medecin_email: string | null;
  documents: number;
  comptes_rendus_manquants: string[];
  patient: PatientLink | null;
  /**
   * Not a duplicate of `patient`. A dossier is matched to a record minutes after it arrives, and
   * until then `patient` is null — which is exactly the dossier somebody is most likely to be
   * looking for. The list used to say « Patient non encore rattaché » while the person's own name
   * sat unread in the payload.
   */
  patient_declare: DeclaredName;
  triage_statut: TriageStatut;
  triage_statut_libelle: string | null;
  suite_donnee: SuiteDonnee | null;
  suite_donnee_libelle: string | null;
  triage_note: string | null;
  triage_par: string | null;
  triage_le: string | null;
  historique: Historique | null;
}

export interface Examen {
  examen?: string | null;
  nature?: string | null;
  date_approximative?: string | null;
  lien_images?: string | null;
  code_acces?: string | null;
}

/**
 * The referring physician, as the form transmits them — which is to say **without a name**. The
 * payload carries ADELI, RPPS, an email and the referral letter, and nothing else. `nom` and
 * `prenom` were declared here and never once populated; they are gone rather than left to look like
 * data that failed to arrive.
 */
export interface MedecinTraitant {
  email?: string | null;
  rpps?: string | null;
  adeli?: string | null;
  telephone?: string | null;
}

export interface IdentiteDeclaree {
  nom: string | null;
  prenom: string | null;
  date_naissance: string | null;
  telephone: string | null;
  email: string | null;
  nir: string | null;
}

export interface Reponse {
  cle: string;
  question: string;
  reponse: string | number | boolean | null;
}

export interface Piece {
  document_id: string;
  nom: string | null;
  type: string | null;
  taille_octets: number | null;
}

/** One signature on one compte rendu. Never updated — a second signing writes a second row. */
export interface Avis {
  id: string;
  texte: string;
  suite_donnee: SuiteDonnee;
  praticien: string;
  praticien_rpps: string | null;
  praticien_adeli: string | null;
  valide_le: string;
  /** SHA-256 of the exact PDF bytes that were signed. */
  empreinte: string;
  texte_modifie: boolean;
  modele: string | null;
}

/** What became of one copy of a signed compte rendu. */
export interface Envoi {
  /** "patient" or "medecin_traitant" — who the copy was for, never where it landed. */
  destinataire: string;
  envoye: boolean;
  /** The address it was addressed to, which a redirect does not change. */
  adresse_prevue: string;
  /** Where it actually went. Differs from the above only while a redirect is configured. */
  adresse_reelle: string;
  message_id: string | null;
  /** Why it did not leave, in French, when it did not. */
  raison: string | null;
  /**
   * When this copy was first opened, or null while nobody has. Read live from the link, where what
   * was SENT is frozen on the signature: « envoyé » and « lu » are two facts, and the second one is
   * what the surgeons ask about.
   */
  ouvert_le: string | null;
  ouvertures: number;
}

export interface CompteRendu {
  /** What the model wrote, kept apart from what the practitioner made of it. */
  texte_genere: string | null;
  texte_travail: string | null;
  genere_le: string | null;
  modele: string | null;
  /**
   * The outcome this draft was written FOR. Reloading used to bring the text back without it, so
   * the panel offered a clean slate over a conclusion that already argued one way.
   */
  suite_redigee: SuiteDonnee | null;
  /**
   * What became of each copy, read back from the signature rather than from the response that
   * produced it — so the answer survives a reload, and the other surgeon's session.
   */
  envois: Envoi[];
  avis: Avis | null;
}

export interface DossierDetail extends DossierSummary {
  signaux: Signal[];
  compte_rendu: CompteRendu;
  /** Their priority rule, verbatim. « Prioritaire » is unreadable without it. */
  regle_priorite: string | null;
  identite_declaree: IdentiteDeclaree;
  age: number | null;
  medecin_traitant: MedecinTraitant | null;
  reponses: Reponse[];
  examens: Examen[];
  liens_imagerie: string | null;
  envoye_le: string | null;
  version_trame: string | null;
  duree_remplissage_s: number | null;
  champs_inconnus: Record<string, unknown> | null;
  pieces: Piece[];
}

/**
 * Our two statuses. An avis IS the compte rendu, so there is no state in between.
 *
 * The words are the surgeons' second thought, and they amend ST1 of the rule book validated on
 * 2026-09-16: « Reçu » said what happened to the dossier, « En attente d'avis » says what is owed
 * on it, which is what a queue is read for. The STORED values do not move — `recu` and `avis_rendu`
 * still drive every filter and every sort.
 */
export const STATUT_LABELS: Record<TriageStatut, MessageDescriptor> = {
  recu: msg`En attente d'avis`,
  avis_rendu: msg`Rendu`,
};

export const STATUT_ORDER: TriageStatut[] = ["recu", "avis_rendu"];

/**
 * The orderings a reader may pick BY NAME, beside the default queue.
 *
 * Picking one REPLACES the default rather than nesting inside it. Nested, it would produce a list
 * that is neither a queue nor a chronology, and a reader who cannot say which rule produced a list
 * cannot trust either.
 */
export type Tri = "recent" | "ancien";

export const TRI_LABELS: Record<Tri, MessageDescriptor> = {
  recent: msg`Plus récents d'abord`,
  ancien: msg`Plus anciens d'abord`,
};

export const TRI_ORDER: Tri[] = ["recent", "ancien"];

/** What the avis led to (ST2) — the measure the channel exists to produce. */
export const SUITE_LABELS: Record<SuiteDonnee, MessageDescriptor> = {
  consultation_proposee: msg`Consultation à l'institut proposée`,
  sans_consultation: msg`Compte rendu sans consultation proposée`,
};

export const SUITE_ORDER: SuiteDonnee[] = [
  "consultation_proposee",
  "sans_consultation",
];

export const AXIS_LABELS: Record<string, MessageDescriptor> = {
  urgence_clinique: msg`Urgence clinique`,
  retentissement: msg`Retentissement`,
  parcours_therapeutique: msg`Parcours thérapeutique`,
  intention: msg`Intention du patient`,
  non_classe: msg`Non classé`,
};

/**
 * The shape every tag on this screen shares — the platform's own badge, not an idiom of our own.
 *
 * It was briefly a Jira lozenge: small radius, small caps, tight letter-spacing. Crisper in
 * isolation, and wrong here. `components/ui/badge.tsx` is `rounded-full`, it is used in twenty-six
 * files, and there are twenty-seven more hand-rolled round pills beside it against three square
 * chips in the whole application. Fixing this module's card while inventing its badge traded one
 * inconsistency for another.
 *
 * So the geometry is the platform's, and what this module contributes is the colour map underneath —
 * IMeD's validated vocabulary — plus the one outlined mark that means a delay.
 */
export const TAG =
  "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold";

/**
 * Colour on this screen follows the partner's validated vocabulary, and it is theirs.
 *
 * `ET1` fixes the priority — Prioritaire red, À voir rapidement orange, Standard blue — and `ST1`
 * the statuses, Reçu yellow and Avis rendu green. That spends red on THEIR verdict, which has one
 * consequence worth stating: a dossier whose only strong signal is `G2` (« prêt à envisager une
 * opération ») is « Prioritaire » and therefore red, while setting no delay at all.
 *
 * So the delay marker is deliberately NOT a colour. It is weight — a dark outline, an icon and the
 * word « délai » — which is what lets it sit beside a red badge and still be read as saying
 * something else. A sixth hue there would have competed with the priority instead of qualifying it.
 */
export const PRIORITY_STYLES: Record<string, string> = {
  prioritaire: "bg-red-600 text-white",
  a_voir_rapidement: "bg-orange-500 text-white",
  standard: "bg-blue-100 text-blue-800",
};

export const PRIORITY_STYLE_FALLBACK = "bg-slate-200 text-slate-700";

export const STATUT_STYLES: Record<TriageStatut, string> = {
  recu: "bg-amber-100 text-amber-900",
  avis_rendu: "bg-emerald-100 text-emerald-800",
};

/** ET3 asks for a colour none of the statuses or priorities already uses. */
export const STRONG_TAG_STYLE = "bg-violet-100 text-violet-800";

/**
 * The marker that says « this one sets a delay ».
 *
 * It was drawn in weight rather than hue for a long time, to keep it from competing with the red
 * « Prioritaire » beside it. Rendered, that produced a near-black slab that out-shouted the badge it
 * exists to QUALIFY, and then — softened — a grey outline that said nothing at all.
 *
 * Both were avoiding a hue the house palette does not merely permit here but **reserves** for it:
 * red, « only where a delay for a patient is at stake (télé-expertise triage) ». So it is red, and
 * it is the one OUTLINED mark on a screen where everything else is filled. Beside IMeD's filled red
 * verdict the two now agree — « prioritaire » and « soon » — and in the one case where they do not,
 * a dossier prioritised on `G2` alone, what says so is the absence of this mark rather than a hue
 * the reader has to tell apart from another hue.
 */
export const DELAI_BADGE = "border border-red-300 bg-red-50 text-red-700";

/**
 * « 2ᵉ demande » — the mark that says this person has written to us before.
 *
 * Slate, and deliberately the quietest thing on the line. Every hue here is already spoken for:
 * IMeD's red / orange / blue priorities, their amber / green statuses, violet for the strong tags,
 * and red outlined for the delay. When a hue is taken, the new thing is said in WEIGHT — and this
 * one has the least claim of all to a hue, because it is not a verdict about the patient. It says
 * where to look for context, and nothing about what the context turned out to be.
 */
export const HISTORIQUE_BADGE = "border border-slate-300 bg-white font-medium text-slate-600";

/**
 * The three marks, whatever the dossier's state.
 *
 * A settled dossier used to lose its hues here, on the argument that red says « act now » about work
 * already done. That was wrong, and it was mine rather than theirs. A priority is a PROPERTY of the
 * dossier — what the patient's answers produced — not a call to action, and greying it out destroys
 * the one thing a reader looks back through an answered queue for: which of these was urgent. Every
 * tracker in use keeps a closed ticket's priority visible, for the same reason.
 *
 * What recedes on a settled line is the LINE — a quieter background and a lighter name. The tags keep
 * saying what they always said.
 */
export function priorityStyle(dossier: DossierSummary): string {
  return PRIORITY_STYLES[dossier.priorite_proposee ?? ""] ?? PRIORITY_STYLE_FALLBACK;
}

export function delaiStyle(_dossier: DossierSummary): string {
  return DELAI_BADGE;
}

export function statutStyle(statut: TriageStatut): string {
  return STATUT_STYLES[statut];
}

/**
 * What a signal is ABOUT — and only one of the five earns a filled mark.
 *
 * They were five grey lozenges of slightly different greys, which is the worst of both: too heavy to
 * be quiet, too dull to mean anything, and impossible to tell apart at a glance. Four of them decide
 * nothing on their own — a retentissement, a parcours, an intention qualify a signal the reader is
 * already looking at — so they stop filling a box and become a quiet label.
 *
 * `urgence_clinique` is the one that changes what a surgeon does next, and it is red because that is
 * what the house palette reserves red for: a **delay for a patient**, in this very module. It reads
 * the same as the delay marker in the header, because it says the same thing.
 *
 * `non_classe` keeps its dashed outline: « we have never seen this question » is a statement about
 * our map, not about the patient, and it must not look like a category.
 */
const AXIS_QUIET = "text-[10px] font-semibold uppercase leading-4 tracking-[0.04em] text-slate-500";

export const AXIS_STYLES: Record<string, string> = {
  urgence_clinique: `${TAG} bg-red-100 text-red-800`,
  retentissement: AXIS_QUIET,
  parcours_therapeutique: AXIS_QUIET,
  intention: AXIS_QUIET,
  non_classe: `${TAG} border border-dashed border-slate-300 bg-white text-slate-400`,
};

/**
 * An imaging portal address as typed by a patient, made safe to put in an `href`.
 *
 * Patients write `www.radiologiecorse.com`, or `https//pacs…` with the colon missing. Used as is,
 * the browser resolves either against our own page and opens `/tele-expertise/www…`, which does
 * not exist. So the scheme is supplied or repaired, and anything that is not an http(s) address on
 * a named domain — `javascript:`, a sentence — is refused: the caller shows it as text instead.
 *
 * `site` is the host the link opens, shown beside it because the link text alone never said where
 * a click leads.
 */
export interface ImagingLink {
  href: string;
  site: string;
}

const HTTP_SCHEME = /^(https?)(?::\/*|\/+)/i;
const OTHER_SCHEME = /^[a-z][a-z0-9+-]*:/i;
const DOMAIN = /^(?:[a-z0-9-]+\.)+[a-z]{2,}$/i;

export function imagingLink(raw: string | null | undefined): ImagingLink | null {
  const value = raw?.trim();
  if (!value) return null;

  const scheme = value.match(HTTP_SCHEME);
  let candidate: string;
  if (scheme) candidate = `${scheme[1].toLowerCase()}://${value.slice(scheme[0].length)}`;
  else if (OTHER_SCHEME.test(value)) return null;
  else candidate = `https://${value.replace(/^\/+/, "")}`;

  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  if (!DOMAIN.test(url.hostname)) return null;
  return { href: url.href, site: url.hostname.replace(/^www\./, "") };
}

/** A received date as « 15/09/2026 », or null when the dossier carries none. */
export function formatDate(value: string | null, locale: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(locale || "fr", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** A received date as « 15 sept. 2026, 11:50 », or null when the dossier carries none. */
export function formatDateTime(
  value: string | null,
  locale: string,
): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(locale || "fr", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** How long a dossier has been waiting, in whole days. */
export function daysWaiting(value: string | null): number | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - date.getTime()) / 86_400_000));
}

/**
 * An answer as one line.
 *
 * Booleans are handed their words by the caller rather than spelt here: a bare `false` on screen
 * reads as a missing answer, and the words are user-facing text, so they belong to the catalogue.
 */
export function formatAnswer(
  value: Signal["reponse"],
  words: { oui: string; non: string },
): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value ? words.oui : words.non;
  return String(value);
}

/** « Lucie ESMINGAUD », or null when nothing names this person yet. */
export function patientName(patient: PatientLink | null): string | null {
  if (!patient) return null;
  const full = [patient.prenom, patient.nom].filter(Boolean).join(" ").trim();
  return full || null;
}

/**
 * The name to put on a row: the matched record's, else the one the patient typed.
 *
 * In that order on purpose. Once a dossier is attached to a record, the record is what the rest of
 * the platform calls this person, and two screens naming them differently is worse than one naming
 * them late.
 */
export function dossierName(dossier: DossierSummary): string | null {
  const matched = patientName(dossier.patient);
  if (matched) return matched;
  const declared = [dossier.patient_declare?.prenom, dossier.patient_declare?.nom]
    .filter(Boolean)
    .join(" ")
    .trim();
  return declared || null;
}

/**
 * The answers, split where the key's letter changes.
 *
 * `answers_in_order` already returns them in the order the patient met them, sorted by the sending
 * site's own step list — so a change of letter IS a change of step. Grouping on it reads the form's
 * structure out of the data instead of guessing it.
 *
 * What it deliberately does NOT do is give the steps names. The payload carries none, and « Douleur »
 * or « Antécédents » over a block would be our words presented as theirs, wrong the day they reorder
 * a question. The letter is what the screen already prints beside each question, so it is what the
 * block is labelled with, until IMeD sends the real ones.
 */
export interface ReponseSection {
  lettre: string;
  reponses: Reponse[];
}

export function answersBySection(reponses: Reponse[]): ReponseSection[] {
  const sections: ReponseSection[] = [];
  for (const reponse of reponses) {
    const lettre = (reponse.cle.match(/^[A-Za-z]+/)?.[0] ?? "?").toUpperCase();
    const current = sections[sections.length - 1];
    if (current && current.lettre === lettre) current.reponses.push(reponse);
    else sections.push({ lettre, reponses: [reponse] });
  }
  return sections;
}

/**
 * The compte rendu as Markdown-lite blocks: `## ` is a heading, `- ` a bullet, the rest prose.
 *
 * The same shape the PDF renders, so what the practitioner reads on screen and what leaves the
 * building cannot drift apart. Deliberately not a Markdown library: the format is three rules, and
 * a parser that also handles tables and links would accept text the PDF silently drops.
 */
export type ReportBlock =
  | { kind: "heading"; text: string }
  | { kind: "bullet"; text: string }
  | { kind: "paragraph"; text: string };

export function parseReport(texte: string | null): ReportBlock[] {
  const blocks: ReportBlock[] = [];
  let buffer: string[] = [];
  const flush = () => {
    if (buffer.length) {
      blocks.push({ kind: "paragraph", text: buffer.join(" ") });
      buffer = [];
    }
  };
  for (const raw of (texte ?? "").split("\n")) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    if (line.startsWith("#")) {
      flush();
      const text = line.replace(/^#+/, "").trim();
      if (text) blocks.push({ kind: "heading", text });
      continue;
    }
    if (/^[-*\u2022]\s/.test(line)) {
      flush();
      blocks.push({ kind: "bullet", text: line.slice(2).trim() });
      continue;
    }
    buffer.push(line);
  }
  flush();
  return blocks;
}
